import React, { useEffect, useRef } from 'react'
import { /* Navigate , */ Route, Routes, useLocation } from 'react-router-dom'
import { logEvent } from './analytics/analytics'
import AuthGuard from './guard/AuthGuard'
import RoleGuard from './guard/RoleGuard'
import { /* CHILD_ROUTES, */ ROUTES } from './helpers/routes-helper'
import BaseLayout from './layouts/BaseLayout'
import { PromoModalProvider } from './context/PromoModalContext'
import { PlannerModeProvider } from './context/PlannerModeContext'
import PlannerModeBar from './components/planner/PlannerModeBar'
import OnboardLayout from './layouts/OnboardLayout'
import WebMobileLayout from './layouts/WebMobileLayout'
import RestaurantLayout from './layouts/restaurant/RestaurantLayout'
import About from './pages/eater/about/About'
import Error from './pages/eater/error/Error'
import GroupPage from './pages/eater/group/GroupPage'
import Home from './pages/eater/home/Home'
import Leaderboard from './pages/eater/leaderboard/Leaderboard'
import Onboard from './pages/eater/onboardPages/Onboard'
import Profile from './pages/eater/profile/ProfilePage'
import RewardsInfoPage from './pages/eater/rewards/RewardsInfoPage'
import RecoverAccount from './pages/eater/recoverAccount/RecoverAccount'
import RecoverBlockedAccount from './pages/eater/recoverAccount/RecoverBlockedAccount'
import ReferAndEarnPage from './pages/eater/referAndEarn/ReferAndEarnPage'
import ReferralLandingPage from './pages/referrals/ReferralLandingPage'
import RestaurantDetail from './pages/eater/restaurant/RestaurantDetail'
import Restaurants from './pages/eater/restaurant/Restaurants'
import FoodAreaRestaurantsScreen from './pages/eater/restaurant/FoodAreaRestaurantsScreen'
import SearchResults from './pages/eater/restaurant/SearchResults'
import Social from './pages/eater/social/Social'
// import PartnershipsPage from './pages/influencers/partnerships/PartnershipsPage'
import RestaurantPending from './pages/restaurant/RestaurantPending'
import Dashboard from './pages/restaurant/dashboard/Dashboard'
import Dish from './pages/restaurant/dish/Dish'
// import Campaigns from './pages/restaurant/influencerMarket/Campaigns'
// import FindCreators from './pages/restaurant/influencerMarket/FindCreators'
// import Inbox from './pages/restaurant/influencerMarket/Inbox'
import Menu from './pages/restaurant/menu/Menu'
import Messages from './pages/restaurant/messages/Messages'
import MenuScanResults from './pages/eater/restaurant/MenuScanResults'
import MenuScanLoading from './pages/eater/MenuScanLoading'
import MenuSaveSuccess from './pages/eater/restaurant/MenuSaveSuccess'
import SavedMenuDetailPage from './pages/eater/saved-menu-detail/SavedMenuDetailPage'
import Notifications from './pages/restaurant/notifications/Notifications'
import Recommendations from './pages/restaurant/recommendations/Recommendations'
import Reviews from './pages/restaurant/reviews/Reviews'
import Settings from './pages/restaurant/settings/Settings'
import Sitemap from './sitemap-generator'
import Coupons from './pages/eater/coupons/Coupons'
import RestaurantLeaderboard from './pages/restaurant/leaderboard/Leaderboard'
import Reservations from './pages/eater/reservations/Reservations'
import RestaurantSubscriptionPage from './pages/restaurant/subscription/RestaurantSubscriptionPage'
import EaterSubscriptionPage from './pages/eater/redeem/EaterSubscriptionPage'
import SubscriptionSuccessPage from './pages/eater/redeem/SubscriptionSuccessPage'
import SubscriptionCanceledPage from './pages/eater/redeem/SubscriptionCanceledPage'
import BillingSuccessPage from './pages/billing/BillingSuccessPage'
import BillingCancelPage from './pages/billing/BillingCancelPage'
import MenuScanLoadingDemo from './pages/eater/MenuScanLoadingDemo'
import PlannerLanding from './pages/eater/dine/PlannerLanding'
import DineSessionPage from './pages/eater/dine/DineSessionPage'

/**
 * Derive a coarse screen_name from a pathname (first path segment, or 'home' for '/').
 * Best-effort only — leaves it undefined for empty input.
 */
function deriveScreenName(pathname: string): string | undefined {
  if (!pathname || pathname === '/') {
    return 'home'
  }
  const segment = pathname.split('/').filter(Boolean)[0]
  return segment || undefined
}

const RouterApp: React.FC = () => {
  // RouterApp renders <Routes>, so it is inside the Router context — useLocation is valid here.
  const location = useLocation()
  // Previous pathname, used as the page_view referrer and for consecutive-dupe dedupe.
  // Starts undefined so the very first navigation still emits (referrer omitted on first load;
  // app_opened/home_loaded also cover initial load, but page_view fires here too for consistency).
  const prevPathRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const path = location.pathname
    // Dedupe consecutive identical pathnames (e.g. StrictMode double-invoke, no-op nav).
    if (prevPathRef.current === path) {
      return
    }
    logEvent('page_view', {
      path,
      referrer: prevPathRef.current,
      screen_name: deriveScreenName(path),
    })
    prevPathRef.current = path
  }, [location.pathname])

  return (
    <PromoModalProvider>
      {/* Planner mode wraps the whole route tree (inside the Router, outside Routes) so a planner
          survives navigation between the Planner tab and every restaurant surface, and the
          persistent planner bar can render above all of them. Inert until a planner is started. */}
      <PlannerModeProvider>
      <PlannerModeBar />
      <Routes>
        <Route element={<BaseLayout />}>
          <Route element={<WebMobileLayout />}>
            <Route path="*" element={<Error type="404" />} />
            <Route
              path={ROUTES.UNAUTHORIZED}
              element={<Error type="unauthorized" />}
            />
            {/* Public routes */}
            <Route path="/loading-demo" element={<MenuScanLoadingDemo />} />
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route path={ROUTES.ABOUT} element={<About />} />
            {/* Group Dining Sessions — anon-friendly (no auth wall) */}
            <Route path={ROUTES.DINE} element={<PlannerLanding />} />
            <Route path={ROUTES.DINE_SESSION} element={<DineSessionPage />} />
            <Route path={ROUTES.REWARDS_INFO} element={<RewardsInfoPage />} />
            <Route path="/r/:code" element={<ReferralLandingPage />} />
            {/* <Route path={ROUTES.CAMPAIGNS} element={<PartnershipsPage />} /> */}
            <Route path={ROUTES.RESTAURANTS} element={<Restaurants />} />
            <Route
              path={ROUTES.FOOD_AREA_RESTAURANTS}
              element={<FoodAreaRestaurantsScreen />}
            />
            <Route path={ROUTES.SEARCH} element={<SearchResults />} />
            <Route
              path={ROUTES.MENU_SCAN_LOADING}
              element={<MenuScanLoading />}
            />
            <Route
              path={ROUTES.MENU_SCAN_RESULTS}
              element={<MenuScanResults />}
            />
            <Route
              path={ROUTES.MENU_SAVE_SUCCESS}
              element={<MenuSaveSuccess />}
            />
            <Route
              path={`${ROUTES.RESTAURANT_DETAILS}/:alias/details`}
              element={<RestaurantDetail />}
            />
          </Route>

          {/* Authenticated routes */}
          <Route element={<AuthGuard />}>
            {/* Eater routes */}
            <Route
              element={
                <RoleGuard
                  allowedRoles={['EATER']}
                  allowedStatuses={['ACTIVE']}
                  redirectPath={ROUTES.HOME}
                />
              }
            >
              <Route element={<WebMobileLayout />}>
                {/* <Route */}
                {/*  path={ROUTES.PARTNERSHIPS} */}
                {/*  element={<PartnershipsPage />} */}
                {/* /> */}
                <Route path={ROUTES.SOCIAL} element={<Social />} />
                <Route path={`${ROUTES.SOCIAL}/:id`} element={<GroupPage />} />
                <Route path={ROUTES.PROFILE} element={<Profile />} />
                <Route
                  path={`${ROUTES.SAVED_MENU_DETAIL}/:menuId`}
                  element={<SavedMenuDetailPage />}
                />
                <Route path={ROUTES.POINTS} element={<Leaderboard />} />
                <Route path={ROUTES.RESERVATIONS} element={<Reservations />} />
                <Route
                  path={ROUTES.REFER_AND_EARN}
                  element={<ReferAndEarnPage />}
                />
                <Route
                  path={ROUTES.REDEEM_POINTS}
                  element={<EaterSubscriptionPage />}
                />
                <Route
                  path={ROUTES.SUBSCRIPTION_SUCCESS}
                  element={<SubscriptionSuccessPage />}
                />
                <Route
                  path={ROUTES.SUBSCRIPTION_CANCELED}
                  element={<SubscriptionCanceledPage />}
                />
                <Route
                  path={ROUTES.MY_COUPONS}
                  element={<Coupons name="my-coupon" />}
                />
                <Route
                  path="/billing/success"
                  element={<BillingSuccessPage />}
                />
                <Route path="/billing/cancel" element={<BillingCancelPage />} />
              </Route>
            </Route>

            {/* Onboard routes (for status PENDING) */}
            <Route
              element={
                <RoleGuard
                  allowedRoles={[undefined, 'EATER', 'RESTAURANT']}
                  allowedStatuses={['PENDING', 'ACTIVE', 'NEW']}
                  redirectPath={ROUTES.HOME}
                />
              }
            >
              <Route element={<OnboardLayout />}>
                <Route
                  path={`${ROUTES.ONBOARD}/:stepName`}
                  element={<Onboard />}
                />
              </Route>
            </Route>

            {/* Recovery routes */}
            <Route
              element={
                <RoleGuard
                  allowedRoles={['EATER', 'RESTAURANT']}
                  allowedStatuses={['DELETED']}
                  redirectPath={ROUTES.HOME}
                />
              }
            >
              <Route
                path={ROUTES.RECOVER_ACCOUNT}
                element={<RecoverAccount />}
              />
            </Route>
            <Route
              element={
                <RoleGuard
                  allowedRoles={['EATER', 'RESTAURANT']}
                  allowedStatuses={['BLOCKED']}
                  redirectPath={ROUTES.HOME}
                />
              }
            >
              <Route
                path={ROUTES.RECOVER_BLOCKED_ACCOUNT}
                element={<RecoverBlockedAccount />}
              />
            </Route>

            {/* Restaurant routes  */}
            <Route
              element={
                <RoleGuard
                  allowedRoles={['RESTAURANT']}
                  allowedStatuses={['ACTIVE']}
                  redirectPath={ROUTES.NOTFOUND}
                />
              }
            >
              <Route element={<RestaurantLayout />}>
                <Route
                  path={ROUTES.RESTAURANT_ADMIN_DASHBOARD}
                  element={<Dashboard />}
                />
                <Route path={ROUTES.RESTAURANT_MENUS} element={<Menu />} />
                <Route
                  path={`${ROUTES.RESTAURANT_MENUS}/:id`}
                  element={<Dish />}
                />
                <Route path={ROUTES.RESTAURANT_REVIEWS} element={<Reviews />} />
                <Route
                  path={ROUTES.RESTAURANT_RECOMMENDATIONS}
                  element={<Recommendations />}
                />

                {/* <Route path={ROUTES.RESTAURANT_INFLUENCER}> */}
                {/*  <Route */}
                {/*    index */}
                {/*    element={ */}
                {/*      <Navigate to={CHILD_ROUTES.RESTAURANT_CAMPAIGNS} replace /> */}
                {/*    } */}
                {/*  /> */}
                {/*  <Route */}
                {/*    path={CHILD_ROUTES.RESTAURANT_CAMPAIGNS} */}
                {/*    element={<Campaigns />} */}
                {/*  /> */}
                {/*  <Route */}
                {/*    path={CHILD_ROUTES.RESTAURANT_COLLAB_INBOX} */}
                {/*    element={<Inbox />} */}
                {/*  /> */}
                {/*  <Route */}
                {/*    path={CHILD_ROUTES.RESTAURANT_FIND_CREATORS} */}
                {/*    element={<FindCreators />} */}
                {/*  /> */}
                {/* </Route> */}

                <Route
                  path={ROUTES.RESTAURANT_NOTIFICATIONS}
                  element={<Notifications />}
                />
                <Route
                  path={ROUTES.RESTAURANT_LEADERBOARD}
                  element={<RestaurantLeaderboard />}
                />
                <Route
                  path={ROUTES.RESTAURANT_MESSAGES}
                  element={<Messages />}
                />
                <Route
                  path={ROUTES.RESTAURANT_SETTINGS}
                  element={<Settings />}
                />
                <Route
                  path={ROUTES.RESTAURANT_SUBSCRIPTION}
                  element={<RestaurantSubscriptionPage />}
                />
                <Route
                  path={ROUTES.REDEEM_POINTS_RESTARUANT}
                  element={<Coupons name="redeem-points" />}
                />
              </Route>
            </Route>

            {/* Pending restaurant route */}
            <Route
              element={
                <RoleGuard
                  allowedRoles={['RESTAURANT']}
                  allowedStatuses={['PENDING']}
                  redirectPath={ROUTES.HOME}
                />
              }
            >
              <Route element={<RestaurantLayout />}>
                <Route
                  path={ROUTES.RESTAURANT_ADMIN_PENDING}
                  element={<RestaurantPending />}
                />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path={ROUTES.SITEMAP} element={<Sitemap />} />
      </Routes>
      </PlannerModeProvider>
    </PromoModalProvider>
  )
}

export default RouterApp
